import React from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import { Controller, useWatch } from 'react-hook-form';

import AccordionCard from '../../../Shared/Components/AccordionCard/AccordionCard';
import ReservationAutocomplete from '../ReservationAutocomplete';
import {
  useGetInvoiceCompaniesLookupQuery,
  useGetInvoiceCompanyLookupByIdQuery,
} from '../../Service/companiesLookupApi';

import { InvoiceSchemeOptions } from '../../Types/invoicesLabels';
import { InvoiceScheme, InvoiceType } from '../../Types/invoicesTypes';
import type { CreateInvoiceForm } from '../../Types/createInvoiceFormTypes';
import type { InvoiceBasicsSectionProps } from './InvoiceBasicsSection.types';

import {
  useGetInvoiceSchemesQuery,
  useGetAllowedInvoiceTypesForSchemeWithLabelsQuery,
} from '../../Service/extendedInvoicesApi';
import { useGetCurrenciesQuery } from '../../../ExternalRates/Service/nbpApi';
import type { NbpTableAItem } from '../../../ExternalRates/Service/nbpApi';
import { invoiceLogger } from '../../utils/invoiceLogger';
import { InvoiceTypeLabels } from '../../Types/invoicesLabels';

const InvoiceBasicsSection: React.FC<InvoiceBasicsSectionProps> = ({
  control,
  errors,
  setValue,
  nextNumber,
  isNextLoading,
  isNextError = false,
  nextNumberErrorMessage,
  isCorrectionInvoice = false,
  sellerCompanyId,
  isSellerCompanyIdError = false,
  isReservationLocked = false,
}) => {
  // obserwujemy scheme + type, by reagować na ograniczenia dozwolonych typów
  const scheme = useWatch({ control, name: 'scheme' }) as CreateInvoiceForm['scheme'];
  const type = useWatch({ control, name: 'type' }) as CreateInvoiceForm['type'];
  const reservationId = useWatch({ control, name: 'reservationId' }) as number | undefined;
  const companyId = useWatch({ control, name: 'companyId' }) as number | undefined;
  const shouldUseReservationCompany =
    !!reservationId && !!sellerCompanyId && !isSellerCompanyIdError;
  const selectedCompanyId = shouldUseReservationCompany ? sellerCompanyId : companyId;

  // Fetch companies for manual invoice
  const { data: companies = [], isLoading: isLoadingCompanies } =
    useGetInvoiceCompaniesLookupQuery();

  // Fetch selected company details for display
  const { data: selectedCompany } = useGetInvoiceCompanyLookupByIdQuery(selectedCompanyId!, {
    skip: !selectedCompanyId,
  });

  // --- dozwolone typy dla wybranego schematu ---
  const {
    data: allowedTypesData,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useGetAllowedInvoiceTypesForSchemeWithLabelsQuery(scheme as InvoiceScheme | number | string, {
    skip: scheme === undefined || scheme === null,
  });

  const typeOptions = React.useMemo(
    () =>
      allowedTypesData?.allowedTypes?.map((o) => ({
        value: o.value as InvoiceType,
        label: o.label,
      })) ?? [],
    [allowedTypesData]
  );

  const hasTypeValue = type !== undefined && type !== null && String(type).length > 0;

  const currentTypeOption = React.useMemo(() => {
    if (!hasTypeValue) {
      return null;
    }

    const existingOption = typeOptions.find((option) => option.value === type);
    if (existingOption) {
      return existingOption;
    }

    return {
      value: type as InvoiceType,
      label: InvoiceTypeLabels[type as InvoiceType] ?? String(type),
    };
  }, [hasTypeValue, type, typeOptions]);

  const renderedTypeOptions = React.useMemo(() => {
    if (typeOptions.length > 0) {
      return typeOptions;
    }

    return currentTypeOption ? [currentTypeOption] : [];
  }, [currentTypeOption, typeOptions]);

  React.useEffect(() => {
    const hasRefundInResponse =
      (allowedTypesData?.allowedTypes?.some((option) => option.value === InvoiceType.Refund) ?? false);
    const hasRefundInRenderedOptions = typeOptions.some((option) => option.value === InvoiceType.Refund);

    invoiceLogger.debug('[InvoiceBasicsSection] 📥 allowed types diagnostics', {
      scheme,
      type,
      isTypesLoading,
      isTypesError,
      isCorrectionInvoice,
      allowedTypesRaw: allowedTypesData,
      allowedTypesValues: allowedTypesData?.allowedTypes?.map((option) => option.value) ?? [],
      renderedTypeOptions: typeOptions.map((option) => option.value),
      hasRefundInResponse,
      hasRefundInRenderedOptions,
    });

    if (hasRefundInResponse && !hasRefundInRenderedOptions)
    {
      invoiceLogger.error('[InvoiceBasicsSection] ❌ REFUND lost between API response and rendered options', {
        scheme,
        type,
        allowedTypesRaw: allowedTypesData,
        renderedTypeOptions: typeOptions,
      });
    }
  }, [scheme, type, allowedTypesData, typeOptions, isCorrectionInvoice, isTypesError, isTypesLoading]);

  // Jeśli bieżący type nie jest dozwolony dla aktualnego scheme → ustaw pierwszy dozwolony
  React.useEffect(() => {
    // SKIP: Don't reset type field on correction invoice page (keep default Correction value)
    if (isCorrectionInvoice) return;

    if (isTypesLoading || isTypesError) return;

    if (typeOptions.length === 0 && hasTypeValue) {
      setValue('type', '' as any, { shouldValidate: false });
      return;
    }

    if (!typeOptions.length) return;

    const normalize = (v: unknown) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v);

    const isAllowed = typeOptions.some((o) => normalize(o.value) === normalize(type));
    if (!isAllowed) {
      setValue('type', typeOptions[0].value as any, { shouldValidate: true, shouldDirty: true });
    }
  }, [typeOptions, type, setValue, isCorrectionInvoice, hasTypeValue, isTypesLoading, isTypesError]);

  return (
    <AccordionCard title="Podstawowe dane faktury" icon={<InfoIcon color="primary" />}>
      <Grid container spacing={2}>
        {/* S C H E M E */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="scheme"
            control={control}
            render={({ field }) => (
              <SchemesSelect
                value={field.value}
                onChange={field.onChange}
                error={!!errors.scheme}
                helperText={errors.scheme?.message as string | undefined}
              />
            )}
          />
        </Grid>

        {/* T Y P   F A K T U R Y */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Typ faktury"
                fullWidth
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value as unknown;
                  const normalized =
                    typeof raw === 'string' && /^\d+$/.test(raw)
                      ? Number(raw)
                      : (raw as InvoiceType);
                  field.onChange(normalized as unknown as InvoiceType);
                }}
                error={!!errors.type}
                helperText={
                  (errors.type?.message as string | undefined) ??
                  (isTypesLoading
                    ? 'Ładuję dozwolone typy...'
                    : isTypesError
                      ? 'Nie udało się pobrać dozwolonych typów dla wybranego schematu.'
                      : '')
                }
                disabled={
                  isCorrectionInvoice || isTypesLoading || isTypesError || typeOptions.length === 0
                }
              >
                {renderedTypeOptions.length > 0 ? (
                  renderedTypeOptions.map((opt) => (
                    <MenuItem key={String(opt.value)} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">Brak dostępnych typów</MenuItem>
                )}
              </TextField>
            )}
          />

          <Box mt={1}>
            {isNextLoading ? (
              <Typography variant="body2" color="text.secondary">
                <CircularProgress size={16} /> Pobieram numer...
              </Typography>
            ) : isNextError ? (
              <Typography variant="body2" color="error.main">
                {nextNumberErrorMessage || 'Nie udało się pobrać podglądu numeru faktury.'}
              </Typography>
            ) : nextNumber && (companyId || sellerCompanyId) ? (
              <Typography variant="body2" color="text.secondary">
                Podgląd numeru: <strong>{nextNumber.number}</strong>{' '}
                {nextNumber.isPreview ? '(podgląd)' : ''}
                {nextNumber.note ? ` • ${nextNumber.note}` : ''}
              </Typography>
            ) : !companyId && !sellerCompanyId ? (
              <Typography variant="body2" color="warning.main">
                Wybierz firmę lub rezerwację aby zobaczyć numer faktury
              </Typography>
            ) : null}
          </Box>
        </Grid>

        {/* R E Z E R W A C J A */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="reservationId"
            control={control}
            render={({ field }) => (
              <ReservationAutocomplete
                value={field.value}
                onChange={field.onChange}
                error={!!errors.reservationId}
                helperText={
                  (errors.reservationId?.message as string | undefined) ??
                  (isReservationLocked ? 'Zmiana rezerwacji wymaga korekty faktury' : undefined)
                }
                disabled={isReservationLocked}
              />
            )}
          />
        </Grid>

        {/* F I R M A   W Y S T A W I A J Ą C A  - CONDITIONAL */}
        {shouldUseReservationCompany ? (
          // Scenariusz A: Rezerwacja wybrana - Alert info o auto-resolve
          <Grid size={{ xs: 12, md: 3 }}>
            <Alert severity="info" icon={<BusinessIcon />}>
              <Typography variant="body2">
                <strong>Firma:</strong> {selectedCompany?.name || 'Ładowanie...'}
                <br />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Ustalona automatycznie z oferty (NIP: {selectedCompany?.nip || '...'})
                </Typography>
              </Typography>
            </Alert>
          </Grid>
        ) : (
          // Scenariusz B: Brak rezerwacji - wymagany select
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth required error={!!errors.companyId}>
              <InputLabel id="company-select-label">Firma wystawiająca *</InputLabel>
              <Controller
                name="companyId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="company-select-label"
                    id="company-select"
                    value={field.value || ''}
                    label="Firma wystawiająca *"
                    disabled={isLoadingCompanies}
                  >
                    <MenuItem value="" disabled>
                      <em>-- Wybierz firmę --</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name} (NIP: {company.nip})
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText error={!!errors.companyId}>
                {errors.companyId?.message ||
                  'Każda firma ma osobną numerację faktur (FV/1, FV/2...)'}
              </FormHelperText>
            </FormControl>
          </Grid>
        )}

        {/* W A L U T A */}
        <Grid size={{ xs: 12, md: 2 }}>
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <CurrencySelect
                value={field.value}
                onChange={(v) => field.onChange((v as string).toUpperCase())}
                error={!!errors.currency}
                helperText={errors.currency?.message as string | undefined}
              />
            )}
          />
        </Grid>
      </Grid>
    </AccordionCard>
  );
};

/* =========================
   Internal: SchemesSelect
   ========================= */
type SchemesSelectProps = {
  value: CreateInvoiceForm['scheme'];
  onChange: (value: CreateInvoiceForm['scheme']) => void;
  error?: boolean;
  helperText?: string;
};

const SchemesSelect: React.FC<SchemesSelectProps> = ({ value, onChange, error, helperText }) => {
  const { data, isLoading, isError } = useGetInvoiceSchemesQuery();

  const normalizeScheme = (v: unknown): InvoiceScheme => {
    if (typeof v === 'number') {
      return v === 1 ? InvoiceScheme.TravelMargin : InvoiceScheme.Standard;
    }
    if (typeof v === 'string') {
      const lowered = v.toLowerCase();
      if (lowered === 'standard') return InvoiceScheme.Standard;
      if (lowered === 'travelmargin') return InvoiceScheme.TravelMargin;
      return v as InvoiceScheme;
    }
    return InvoiceScheme.Standard;
  };

  const hint =
    value === InvoiceScheme.TravelMargin ? 'Procedura marży dla biur podróży' : undefined;

  if (isLoading) {
    return (
      <TextField
        select
        label="Schemat"
        value={value ?? ''}
        fullWidth
        disabled
        helperText={hint}
        error={!!error}
      >
        {InvoiceSchemeOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (isError || !data || data.length === 0) {
    // Fallback do statycznych opcji
    return (
      <TextField
        select
        label="Schemat"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value as InvoiceScheme)}
        fullWidth
        error={!!error}
        helperText={helperText ?? hint}
      >
        {InvoiceSchemeOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  const options = data.map((s) => ({
    value: normalizeScheme(s.scheme),
    label: s.schemeLabel,
  }));

  return (
    <TextField
      select
      label="Schemat"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value as InvoiceScheme)}
      fullWidth
      error={!!error}
      helperText={helperText ?? hint}
    >
      {options.map((opt) => (
        <MenuItem key={String(opt.value)} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

/* =========================
   Internal: CurrencySelect
   ========================= */
type CurrencySelectProps = {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
};

const CurrencySelect: React.FC<CurrencySelectProps> = ({ value, onChange, error, helperText }) => {
  const { data, isLoading, isError } = useGetCurrenciesQuery();

  if (isLoading) {
    return (
      <TextField
        label="Waluta"
        value={value ?? ''}
        fullWidth
        disabled
        helperText="Ładuję waluty NBP..."
        error={!!error}
      />
    );
  }

  if (isError || !data || data.length === 0) {
    // Fallback: ręczne wpisanie (np. PLN)
    return (
      <TextField
        label="Waluta"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        fullWidth
        error={!!error}
        helperText={helperText ?? 'Wpisz kod waluty (np. PLN, EUR, USD)'}
      />
    );
  }

  const codes = Array.from(
    new Set<string>([
      'PLN',
      ...data
        .map((r: NbpTableAItem) => r.code?.toUpperCase())
        .filter((x): x is string => Boolean(x)),
    ])
  );
  if (value && !codes.includes(String(value).toUpperCase())) {
    codes.unshift(String(value).toUpperCase());
  }

  return (
    <TextField
      select
      label="Waluta"
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value as string).toUpperCase())}
      fullWidth
      error={!!error}
      helperText={helperText ?? 'Źródło: NBP tabela A'}
    >
      {codes.map((c) => (
        <MenuItem key={c} value={c}>
          {c}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default InvoiceBasicsSection;
